"use client";

import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";

import { Avatar } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

interface TypingIndicatorProps {
  users: { [key: string]: { timestamp: number; user: any } };
  className?: string;
}

export function TypingIndicator({ users, className }: TypingIndicatorProps) {
  const { t } = useTranslation(["chat"]);
  const typingUsers = Object.values(users);

  if (typingUsers.length === 0) return null;

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      className={cn("flex items-center gap-3 rounded-2xl px-4 backdrop-blur-xl", className)}
      exit={{ opacity: 0, y: 10 }}
      initial={{ opacity: 0, y: 10 }}
      transition={{ duration: 0.2 }}
    >
      <div className="flex -space-x-2">
        {typingUsers.slice(0, 3).map((userData, index) => (
          <Avatar key={index} className="h-6 w-6 shadow-sm">
            <img
              alt={`${userData.user.first_name || ""} ${userData.user.last_name || ""}`}
              src={userData.user.avatar_path || "/placeholder.svg?height=24&width=24"}
            />
          </Avatar>
        ))}
      </div>

      <div className="text-muted-foreground flex items-center text-xs font-medium">
        <span>
          {typingUsers.length === 1
            ? t("chat:typing.oneUser", { name: typingUsers[0].user.first_name || "Someone" })
            : typingUsers.length === 2
              ? t("chat:typing.twoUsers", {
                  first: typingUsers[0].user.first_name || "Someone",
                  second: typingUsers[1].user.first_name || "Someone",
                })
              : t("chat:typing.manyUsers", { count: typingUsers.length })}
        </span>
        <div className="ml-1.5 flex items-center">
          <Icon className="text-accent-foreground h-3 w-3" icon="svg-spinners:3-dots-fade" />
        </div>
      </div>
    </motion.div>
  );
}
